'use strict';
import React, { useEffect, useMemo, useState } from 'react';
import { FiPlus, FiEdit2, FiPause, FiPlay, FiXCircle, FiEyeOff, FiZap, FiSearch, FiX, FiChevronLeft, FiChevronRight, FiChevronDown, FiExternalLink, FiEye, FiCreditCard } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api, { SeriesAPI } from '../services/api';
import HeaderDateFilters from './Tasks/components/Filters/HeaderDateFilters';
import CustomerDetail from './CustomerDetail';
import PaymentModal from '../components/PaymentModal';
import { colors, matte, tableStyles, billingTableCSS } from '../styles/billingTableStyles';

const getCurrentFiscalYearRange = () => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const fyStartMonth = 3; // April (0-indexed)
  const fyStartYear = now.getMonth() >= fyStartMonth ? now.getFullYear() : now.getFullYear() - 1;
  const start = new Date(fyStartYear, fyStartMonth, 1, 0, 0, 0, 0);
  const end = new Date(fyStartYear + 1, 2, 31, 23, 59, 59, 999);
  return { start, end };
};

const styles = {
  page: {
    minHeight: '100vh',
    background: `linear-gradient(135deg, ${colors.stone50} 0%, ${colors.stone100} 100%)`,
    padding: '24px 32px',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 12,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: colors.stone800,
    margin: 0,
    letterSpacing: '-0.02em',
  },
  subtitle: {
    fontSize: 13,
    color: colors.stone500,
    margin: '2px 0 0',
  },
  headerActions: {
    display: 'flex',
    gap: 8,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  card: {
    background: '#fff',
    borderRadius: 16,
    border: `1px solid ${colors.stone200}`,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.02)',
    marginBottom: 16,
    overflow: 'hidden',
  },
  cardHeader: {
    padding: '12px 16px',
    borderBottom: `1px solid ${colors.stone100}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    flexWrap: 'wrap',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: 12,
  },
  statCard: {
    background: colors.stone800,
    borderRadius: 12,
    padding: 14,
    border: `1px solid ${colors.stone700}`,
  },
  statTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.stone300,
    marginBottom: 10,
  },
  segBar: {
    display: 'flex',
    height: 8,
    borderRadius: 4,
    overflow: 'hidden',
    background: colors.stone700,
  },
  legendGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 6,
    marginTop: 10,
  },
  legendItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '6px 8px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    fontSize: 11,
    color: '#fff',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
  },
  legendItemActive: {
    border: '1px solid rgba(255,255,255,0.5)',
    background: 'rgba(255,255,255,0.1)',
  },
  searchWrapper: {
    position: 'relative',
    flex: 1,
    maxWidth: 280,
    minWidth: 180,
  },
  searchIcon: {
    position: 'absolute',
    left: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    color: colors.stone400,
    pointerEvents: 'none',
  },
  searchInput: {
    width: '100%',
    padding: '7px 10px 7px 32px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: '#fff',
    color: colors.stone800,
    outline: 'none',
  },
  select: {
    padding: '7px 26px 7px 10px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: '#fff',
    color: colors.stone700,
    cursor: 'pointer',
    outline: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2378716c' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 8px center',
  },
  ...tableStyles,
  btnPrimary: {
    padding: '7px 12px',
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    background: `linear-gradient(135deg, ${colors.accent500} 0%, ${colors.accent600} 100%)`,
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    boxShadow: '0 2px 6px rgba(6, 182, 212, 0.25)',
  },
  pagination: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  pageBtn: {
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fff',
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 6,
    color: colors.stone600,
    cursor: 'pointer',
  },
  toast: {
    position: 'fixed',
    bottom: 24,
    right: 24,
    padding: '10px 16px',
    background: colors.stone800,
    color: '#fff',
    borderRadius: 10,
    fontSize: 12,
    fontWeight: 500,
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    animation: 'slideUp 0.3s ease',
  },
  modal: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28, 25, 23, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  modalContent: {
    background: '#fff',
    borderRadius: 16,
    width: '100%',
    maxWidth: 520,
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    padding: '16px 20px',
    borderBottom: `1px solid ${colors.stone200}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalBody: {
    padding: '16px 20px',
  },
  modalFooter: {
    padding: '12px 20px',
    borderTop: `1px solid ${colors.stone100}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 8,
  },
  drawer: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(28, 25, 23, 0.5)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    justifyContent: 'flex-end',
    zIndex: 1000,
  },
  drawerContent: {
    width: '60%',
    maxWidth: 900,
    height: '100%',
    background: '#fff',
    boxShadow: '-8px 0 32px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
  },
  formGroup: {
    marginBottom: 12,
  },
  label: {
    display: 'block',
    fontSize: 11,
    fontWeight: 600,
    color: colors.stone500,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 4,
  },
  input: {
    width: '100%',
    padding: '8px 10px',
    fontSize: 12,
    border: `1.5px solid ${colors.stone200}`,
    borderRadius: 8,
    background: colors.stone50,
    color: colors.stone800,
    outline: 'none',
  },
};

const getStatusDotColor = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'ACTIVE') return matte.green;
  if (s === 'PAUSED') return matte.amber;
  if (s === 'CANCELLED') return matte.red;
  if (s === 'EXPIRED') return matte.grey;
  return matte.grey;
};

export default function RecurringInvoices() {
  const { company } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState(() => {
    const { start, end } = getCurrentFiscalYearRange();
    const toYMD = (iso) => new Date(iso).toISOString().slice(0, 10);
    return { customerId: '', fromDate: toYMD(start.toISOString()), toDate: toYMD(end.toISOString()) };
  });
  const [statusView, setStatusView] = useState('');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerDrawer, setCustomerDrawer] = useState({ open: false, id: null, customer: null });
  const [showStats, setShowStats] = useState(() => {
    try { return JSON.parse(window.localStorage.getItem('recurringInvoices.showStats') ?? 'true'); } catch { return true; }
  });
  const [formatModal, setFormatModal] = useState(false);
  const [formatOptions, setFormatOptions] = useState([]);
  const [selectedFormatId, setSelectedFormatId] = useState(null);
  const [formatActivatingId, setFormatActivatingId] = useState(null);
  const [seriesState, setSeriesState] = useState({ loading: false, saving: false, id: null, prefix: '', prefixPre: '', suffix: '', currentSeq: 0, error: '', fy: '', fySep: '/' });
  const [details, setDetails] = useState({ open: false, loading: false, data: null, error: '' });
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [pdfPreview, setPdfPreview] = useState({ open: false, url: '', label: '', kind: 'Proforma', loading: false });
  const [proformaPayModal, setProformaPayModal] = useState({ open: false, proformaId: null });

  const submitProformaPayment = async (paymentData) => {
    try {
      await api.post(`/api/proformas/${proformaPayModal.proformaId}/convert-to-invoice`, paymentData);
      setToast('Invoice created');
      setProformaPayModal({ open: false, proformaId: null });
      if (details?.data?.recurringInvoice?.id) {
        const { data } = await api.get(`/api/recurring-invoices/${details.data.recurringInvoice.id}/history`);
        setDetails(d => ({ ...d, data }));
      }
      await fetchRows();
    } catch (e) { setToast(e?.response?.data?.error || 'Failed to create invoice'); }
  };
  const [payInfo, setPayInfo] = useState({ open: false, loading: false, inv: null, error: '' });
  const [dateRange, setDateRange] = useState(() => {
    const { start, end } = getCurrentFiscalYearRange();
    return { startDate: start.toISOString(), endDate: end.toISOString(), label: 'Current FY' };
  });
  const [modal, setModal] = useState({
    open: false,
    form: {
      name: '',
      customerId: '',
      billingInterval: 'MONTH',
      intervalCount: 1,
      billingAnchor: 'ANNIVERSARY',
      dayOfMonth: '',
      startDate: new Date().toISOString().slice(0, 10),
      endDate: '',
      dueDays: 7,
      currency: 'INR',
      posStateCode: '',
      terms: '',
      notes: '',
      taxInclusive: false,
      items: []
    },
    saving: false,
    error: ''
  });

  useEffect(() => { try { window.localStorage.setItem('recurringInvoices.showStats', JSON.stringify(showStats)); } catch {} }, [showStats]);

  const statusCounts = useMemo(() => {
    const counts = { ACTIVE: 0, PAUSED: 0, CANCELLED: 0, OTHER: 0 };
    for (const r of (rows || [])) {
      const s = String(r?.status || '').toUpperCase();
      if (s === 'ACTIVE') counts.ACTIVE += 1;
      else if (s === 'PAUSED') counts.PAUSED += 1;
      else if (s === 'CANCELLED') counts.CANCELLED += 1;
      else counts.OTHER += 1;
    }
    return counts;
  }, [rows]);

  const dueCounts = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    let overdue = 0, dueToday = 0, upcoming = 0;
    for (const r of (rows || [])) {
      const lastInvoiceDate = String(r?.lastInvoiceDate || '');
      const nextInvoiceDate = String(r?.nextInvoiceDate || '');
      const lastInvoiceNumber = r?.lastInvoiceNumber;
      if (lastInvoiceNumber && lastInvoiceDate < nextInvoiceDate) { upcoming += 1; continue; }
      if (nextInvoiceDate === today && lastInvoiceDate !== today) { dueToday += 1; continue; }
      if (nextInvoiceDate && nextInvoiceDate > today && lastInvoiceDate !== today) { overdue += 1; continue; }
      if ((!lastInvoiceNumber || lastInvoiceDate < nextInvoiceDate) && nextInvoiceDate > today) { upcoming += 1; }
    }
    return { overdue, dueToday, upcoming };
  }, [rows]);

  const isStatActive = (key) => {
    const s = String(statusView || '').toUpperCase();
    if (key === 'ALL') return !s;
    return s === key;
  };

  const applyStatFilters = (patch) => setStatusView(String(patch?.status || '').toUpperCase());

  const resolveCustomerName = (row) => {
    const id = row?.customer?.id || row?.customerSnapshot?.id || row?.customerId;
    if (row?.customer?.name) return row.customer.name;
    if (row?.customerSnapshot?.name) return row.customerSnapshot.name;
    if (!id) return '-';
    const c = customers.find(x => String(x.id) === String(id));
    return c?.name || `Customer ${id}`;
  };

  const visibleRows = useMemo(() => {
    const s = String(statusView || '').toUpperCase();
    const q = String(search || '').trim().toLowerCase();
    return (rows || []).filter((r) => {
      if (s && String(r?.status || '').toUpperCase() !== s) return false;
      if (!q) return true;
      const cust = String(resolveCustomerName(r) || '').toLowerCase();
      const name = String(r?.name || '').toLowerCase();
      const proforma = String(r?.lastProformaNumber || '').toLowerCase();
      const invoice = String(r?.lastInvoiceNumber || '').toLowerCase();
      return cust.includes(q) || name.includes(q) || proforma.includes(q) || invoice.includes(q);
    });
  }, [rows, statusView, search, customers]);

  const params = useMemo(() => {
    const base = { page, pageSize, ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v)) };
    if (company?.id) base.companyId = company.id;
    return base;
  }, [company?.id, page, pageSize, filters]);

  const handleDateChange = (val) => {
    if (val === 'all') { setDateRange({ startDate: null, endDate: null }); setFilters((f) => ({ ...f, fromDate: '', toDate: '' })); setPage(1); return; }
    const toYMD = (iso) => new Date(iso).toISOString().slice(0, 10);
    setDateRange(val);
    setFilters((f) => ({ ...f, fromDate: toYMD(val.startDate), toDate: toYMD(val.endDate) }));
    setPage(1);
  };

  const fetchCustomers = async () => {
    if (!company?.id) return;
    setCustomersLoading(true);
    try {
      const { data } = await api.get(`/api/companies/${company.id}/customers`);
      const list = Array.isArray(data) ? data : (Array.isArray(data?.customers) ? data.customers : (Array.isArray(data?.data) ? data.data : []));
      setCustomers((list || []).map(c => ({ id: c.id, name: c.name, phone: c.phone || c.mobile || c.whatsapp || '' })));
    } catch {} finally { setCustomersLoading(false); }
  };

  const openCustomerDetail = (row) => {
    const id = row?.customer?.id || row?.customerSnapshot?.id || row?.customerId;
    if (!id) return;
    const customer = row?.customer || customers.find(c => String(c.id) === String(id)) || null;
    setCustomerDrawer({ open: true, id, customer });
  };
  const closeCustomerDrawer = () => setCustomerDrawer({ open: false, id: null, customer: null });

  const fetchRows = async () => {
    if (!company?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get('/api/recurring-invoices', { params });
      setRows(data?.data || []);
      setTotal(data?.total || (data?.data?.length || 0));
    } catch { setToast('Failed to load recurring invoices'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRows(); }, [company?.id, page, pageSize, filters]);
  useEffect(() => { if (company?.id) fetchCustomers(); }, [company?.id]);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(''), 3000); return () => clearTimeout(t); }, [toast]);

  useEffect(() => {
    if (!company?.id) return;
    (async () => {
      try {
        const { data: comp } = await api.get('/api/companies/' + company.id);
        setCompanyData(comp);
        try {
          const { data: gstStatus } = await api.get(`/api/companies/${company.id}/gst-status`);
          setCompanyData(prev => ({ ...(prev || comp || {}), gstin: gstStatus?.gstin ?? (prev?.gstin ?? comp?.gstin), stateCode: gstStatus?.stateCode ?? (prev?.stateCode ?? comp?.stateCode) }));
        } catch {}
      } catch {}
    })();
  }, [company?.id]);

  const formatINR = (val) => {
    try { return '₹' + new Intl.NumberFormat('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(Number(val || 0)); } catch { return val; }
  };

  const calcTemplateTotal = (rec) => {
    const companyState = companyData?.stateCode || '';
    const pos = rec?.posStateCode || '';
    const isInter = companyState && pos && companyState !== pos;
    const noGst = !companyData?.gstin;
    let taxable = 0, cgst = 0, sgst = 0, igst = 0, cess = 0;
    for (const l of (rec?.items || [])) {
      const qty = Number(l.quantity || 0);
      const price = Number(l.price || 0);
      const gross = qty * price;
      const discAmt = Math.max(0, Number(l.discountAmount || 0));
      const discPct = Math.max(0, Math.min(100, Number(l.discountPercent || 0)));
      const discFromPct = +((gross * discPct) / 100).toFixed(2);
      const lineDisc = discAmt > 0 ? Math.min(gross, discAmt) : Math.min(gross, discFromPct);
      const net = +Math.max(0, gross - lineDisc).toFixed(2);
      taxable += net;
      if (noGst) continue;
      const cgRate = isInter ? 0 : Number(l.cgstRate || 0);
      const sgRate = isInter ? 0 : Number(l.sgstRate || 0);
      const igRate = isInter ? Number(l.igstRate || 0) : 0;
      const csRate = Number(l.cessRate || 0);
      cgst += +((net * cgRate) / 100).toFixed(2);
      sgst += +((net * sgRate) / 100).toFixed(2);
      igst += +((net * igRate) / 100).toFixed(2);
      cess += +((net * csRate) / 100).toFixed(2);
    }
    taxable = +taxable.toFixed(2);
    return +(taxable + (noGst ? 0 : (cgst + sgst + igst + cess))).toFixed(2);
  };

  const proformaNumberStyle = (row) => {
    const status = String(row?.lastProformaStatus || '').toUpperCase();
    const paymentStatus = String(row?.lastProformaPaymentStatus || '').toUpperCase();
    // IMPORTANT: Do NOT treat "any invoice exists" as "this last proforma is paid".
    // lastInvoiceId is for the recurring invoice overall, not necessarily derived from lastProformaId.
    const paid = status === 'PAID' || paymentStatus === 'PAID' || status === 'CONVERTED';
    const partial = paymentStatus === 'PARTIALLY_PAID';
    const unpaid = paymentStatus === 'UNPAID' || (!paid && !partial && (status === 'DRAFT' || status === 'SENT' || status === 'CONVERTED'));
    if (paid) return { color: colors.emerald600 };
    if (partial) return { color: colors.amber600 };
    if (unpaid) return { color: colors.rose600 };
    return { color: colors.stone700 };
  };

  const isLastProformaUnpaid = (row) => {
    if (!row?.lastProformaId) return false;
    const status = String(row?.lastProformaStatus || '').toUpperCase();
    const paymentStatus = String(row?.lastProformaPaymentStatus || '').toUpperCase();
    if (paymentStatus === 'PAID' || paymentStatus === 'PARTIALLY_PAID') return false;
    if (status === 'PAID' || status === 'CONVERTED') return false;
    return paymentStatus === 'UNPAID' || status === 'SENT' || status === 'DRAFT';
  };

  // Series state initialization
  useEffect(() => {
    if (!company?.id) return;
    (async () => {
      setSeriesState(s => ({ ...s, loading: true }));
      try {
        const cur = await SeriesAPI.current(company.id, 'PROFORMA');
        const fyFromBounds = (() => {
          if (cur.fyStart && cur.fyEnd) {
            const y1 = String(new Date(cur.fyStart).getUTCFullYear()).slice(-2);
            const y2 = String(new Date(cur.fyEnd).getUTCFullYear()).slice(-2);
            return `${y1}-${y2}`;
          }
          return '';
        })();
        const fullPrefix = cur.prefix || '';
        const fyIdx = fyFromBounds ? fullPrefix.indexOf(fyFromBounds) : -1;
        let prefixPre = fullPrefix, fySep = '/';
        if (fyIdx >= 0) {
          prefixPre = fullPrefix.slice(0, fyIdx);
          const charBefore = fullPrefix[fyIdx - 1];
          if (charBefore && '/-_ .'.includes(charBefore)) { fySep = charBefore; if (prefixPre.endsWith(fySep)) prefixPre = prefixPre.slice(0, -1); }
        }
        setSeriesState({ loading: false, saving: false, id: cur.id, prefix: fullPrefix, prefixPre, suffix: cur.suffix || '', currentSeq: cur.currentSeq || 0, error: '', fy: fyFromBounds, fySep });
      } catch {
        const computeFY = () => { const dt = new Date(), yr = dt.getUTCFullYear(), mon = dt.getUTCMonth() + 1; const s = mon >= 4 ? yr : yr - 1, e = mon >= 4 ? yr + 1 : yr; return `${String(s).slice(-2)}-${String(e).slice(-2)}`; };
        setSeriesState({ loading: false, saving: false, id: null, prefix: '', prefixPre: '', suffix: '', currentSeq: 0, error: '', fy: computeFY(), fySep: '/' });
      }
    })();
  }, [company?.id]);

  useEffect(() => {
    if (!company?.id || !formatModal) return;
    SeriesAPI.list({ companyId: company.id, documentType: 'PROFORMA' }).then(list => setFormatOptions(Array.isArray(list) ? list : [])).catch(() => {});
  }, [company?.id, formatModal]);

  const saveSeriesFormat = async () => {
    if (!company?.id) return;
    setSeriesState(s => ({ ...s, saving: true }));
    try {
      const composedPrefix = `${seriesState.prefixPre || ''}${seriesState.fySep || '/'}${seriesState.fy || ''}${seriesState.fySep || '/'}`;
      const targetDate = seriesState.fy ? `20${seriesState.fy.slice(0, 2)}-04-01` : new Date().toISOString().slice(0, 10);
      await SeriesAPI.setFormat({ companyId: company.id, documentType: 'PROFORMA', prefix: composedPrefix, suffix: seriesState.suffix || '', date: targetDate });
      const [cur, list] = await Promise.all([SeriesAPI.current(company.id, 'PROFORMA'), SeriesAPI.list({ companyId: company.id, documentType: 'PROFORMA' })]);
      setFormatOptions(Array.isArray(list) ? list : []);
      setSeriesState(s => ({ ...s, saving: false, id: cur.id, prefix: cur.prefix || composedPrefix, currentSeq: cur.currentSeq || s.currentSeq }));
      setToast('Format saved');
      setFormatModal(false);
    } catch { setSeriesState(s => ({ ...s, saving: false })); setToast('Failed to save'); }
  };

  const applyExistingFormat = async (row) => {
    setFormatActivatingId(row.id);
    try {
      const targetDate = row.fyStart ? String(row.fyStart).slice(0, 10) : new Date().toISOString().slice(0, 10);
      await SeriesAPI.setFormat({ companyId: company.id, documentType: 'PROFORMA', prefix: row.prefix, suffix: row.suffix || '', date: targetDate });
      const [cur, list] = await Promise.all([SeriesAPI.current(company.id, 'PROFORMA'), SeriesAPI.list({ companyId: company.id, documentType: 'PROFORMA' })]);
      setFormatOptions(Array.isArray(list) ? list : []);
      setSeriesState(s => ({ ...s, id: cur.id, currentSeq: cur.currentSeq || s.currentSeq }));
      setSelectedFormatId(row.id);
    } catch {} finally { setFormatActivatingId(null); }
  };

  const openCreate = () => {
    if (!customers.length) fetchCustomers();
    setModal({ open: true, saving: false, error: '', form: { ...modal.form, customerId: '', name: '' } });
  };
  const closeModal = () => setModal({ open: false, saving: false, error: '', form: { ...modal.form } });

  const validateForm = () => {
    const f = modal.form;
    if (!f.name?.trim()) return 'Please enter a name';
    if (!f.customerId) return 'Please select a customer';
    if (f.billingInterval === 'CUSTOM' && (!f.intervalCount || Number(f.intervalCount) < 1)) return 'Enter a valid interval in months';
    if (f.billingAnchor === 'DAY_OF_MONTH') {
      const d = Number(f.dayOfMonth);
      if (!d || d < 1 || d > 28) return 'Choose a billing day between 1 and 28';
    }
    return '';
  };

  const save = async () => {
    const err = validateForm();
    if (err) { setModal(m => ({ ...m, error: err })); return; }
    try {
      setModal(m => ({ ...m, saving: true, error: '' }));
      const payload = { ...modal.form, companyId: company.id };
      const { data } = await api.post('/api/recurring-invoices', payload);
      setRows(prev => [data, ...prev]);
      closeModal();
      navigate(`/recurring-invoice/${data.id}/edit`);
    } catch { setModal(m => ({ ...m, saving: false, error: 'Failed to save. Please try again.' })); }
  };

  const invoiceNow = async (id) => {
    try { await api.post(`/api/recurring-invoices/${id}/invoice-now`); setToast('Proforma created'); await fetchRows(); }
    catch { setToast('Failed to create proforma'); }
  };

  const updateStatus = async (id, status) => {
    try {
      if (status === 'CANCELLED') {
        const ok = window.confirm('Cancel this recurring invoice?\n\nThis will stop future proformas from being generated. You can re-activate later, but it is treated as a “stopped” template.');
        if (!ok) return;
      }
      if (status === 'PAUSED') {
        const ok = window.confirm('Pause this recurring invoice?\n\nNo proformas will be generated while paused.');
        if (!ok) return;
      }
      setStatusUpdatingId(id);
      await api.post(`/api/recurring-invoices/${id}/status`, { status });
      await fetchRows();
      setToast(`Status set to ${status}`);
    } catch { setToast('Failed to update status'); }
    finally { setStatusUpdatingId(null); }
  };

  const openDetails = async (id) => {
    try {
      setDetails({ open: true, loading: true, data: null, error: '' });
      const { data } = await api.get(`/api/recurring-invoices/${id}/history`);
      setDetails({ open: true, loading: false, data, error: '' });
    } catch { setDetails({ open: true, loading: false, data: null, error: 'Failed to load details' }); }
  };
  const closeDetails = () => setDetails({ open: false, loading: false, data: null, error: '' });
  const closePayInfo = () => setPayInfo({ open: false, loading: false, inv: null, error: '' });

  const openPaymentDetails = async (row) => {
    const invoiceId = row?.lastInvoiceId;
    if (!invoiceId) { setPayInfo({ open: true, loading: false, inv: null, error: 'No invoice found yet.' }); return; }
    try {
      setPayInfo({ open: true, loading: true, inv: null, error: '' });
      const { data: inv } = await api.get(`/api/invoices/${invoiceId}`);
      setPayInfo({ open: true, loading: false, inv, error: '' });
    } catch { setPayInfo({ open: true, loading: false, inv: null, error: 'Failed to load payment details' }); }
  };

  const openProformaPdf = async (id, label) => {
    try {
      setPdfPreview({ open: true, url: '', label: label || '', kind: 'Proforma', loading: true });
      const { data } = await api.get(`/api/proformas/${id}/pdf-url`);
      if (!data?.url) throw new Error('No URL');
      setPdfPreview(p => ({ ...p, url: data.url, loading: false }));
    } catch { setToast('Failed to load PDF'); setPdfPreview({ open: false, url: '', label: '', kind: 'Proforma', loading: false }); }
  };
  const closePdfPreview = () => setPdfPreview({ open: false, url: '', label: '', kind: 'Proforma', loading: false });

  const openInvoicePdf = async (id, label) => {
    try {
      setPdfPreview({ open: true, url: '', label: label || '', kind: 'Invoice', loading: true });
      const { data } = await api.get(`/api/invoices/${id}/pdf-url`);
      if (!data?.url) throw new Error('No URL');
      setPdfPreview(p => ({ ...p, url: data.url, loading: false }));
    } catch { setToast('Failed to load PDF'); setPdfPreview({ open: false, url: '', label: '', kind: 'Invoice', loading: false }); }
  };

  const getCustomerName = (id) => {
    if (!id) return '-';
    const c = customers.find(x => String(x.id) === String(id));
    return c?.name || `Customer ${id}`;
  };

  const getIntervalMax = (interval) => {
    switch (interval) { case 'MONTH': return 24; case 'QUARTER': return 8; case 'YEAR': return 5; case 'CUSTOM': return 36; default: return 24; }
  };
  const getIntervalExample = (interval, count) => {
    const n = Math.max(1, Number(count || 1));
    if (interval === 'MONTH') return `Every ${n} month${n > 1 ? 's' : ''}`;
    if (interval === 'QUARTER') { const months = 3 * n; if (months % 12 === 0) { const yrs = months / 12; return `Every ${yrs} year${yrs > 1 ? 's' : ''}`; } return `Every ${months} months`; }
    if (interval === 'YEAR') return `Every ${n} year${n > 1 ? 's' : ''}`;
    return `Every ${n} month${n > 1 ? 's' : ''}`;
  };

  const SegmentBar = ({ segments }) => {
    const sum = segments.reduce((a, s) => a + Number(s.value || 0), 0) || 1;
    return (
      <div style={styles.segBar}>
        {segments.map((s, i) => (
          <button key={i} onClick={s.onClick} title={s.title} style={{ width: `${(Number(s.value || 0) / sum) * 100}%`, background: s.color, border: 'none', padding: 0, cursor: s.onClick ? 'pointer' : 'default' }} />
        ))}
      </div>
    );
  };

  const LegendItem = ({ label, value, color, active, onClick }) => (
    <button style={{ ...styles.legendItem, ...(active ? styles.legendItemActive : {}) }} onClick={onClick}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: color }} />
        {label}
      </span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </button>
  );

  const renderPagination = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: `1px solid ${colors.stone100}` }}>
      <span style={{ fontSize: 11, color: colors.stone500 }}>
        Showing <strong style={{ color: colors.stone700 }}>{Math.min((page - 1) * pageSize + 1, total)}-{Math.min(page * pageSize, total)}</strong> of <strong style={{ color: colors.stone700 }}>{total}</strong>
      </span>
      <div style={styles.pagination}>
        <button style={{ ...styles.pageBtn, opacity: page <= 1 ? 0.5 : 1 }} disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}><FiChevronLeft size={14} /></button>
        <span style={{ padding: '0 10px', fontSize: 11, color: colors.stone600 }}>Page {page}</span>
        <button style={{ ...styles.pageBtn, opacity: page * pageSize >= total ? 0.5 : 1 }} disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}><FiChevronRight size={14} /></button>
        <select style={{ ...styles.select, marginLeft: 6 }} value={pageSize} onChange={e => { setPageSize(+e.target.value); setPage(1); }}>
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
        </select>
      </div>
    </div>
  );

  return (
    <div className="page-content">
      <style>{billingTableCSS()}</style>

      {/* Toast */}
      {!!toast && (
        <div style={styles.toast}>
          <span>{toast}</span>
          <button onClick={() => setToast('')} style={{ ...styles.btnIcon, color: '#fff' }}><FiX size={14} /></button>
        </div>
      )}

      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <div>
            <h1 style={styles.title}>Recurring Invoices</h1>
            <p style={styles.subtitle}>{total} templates</p>
          </div>
        </div>
        <div style={styles.headerActions}>
          <HeaderDateFilters value={dateRange} onChange={handleDateChange} />
          <button style={styles.btnSecondary} onClick={() => setFormatModal(true)}>Number Format</button>
          <button
            style={styles.btnSecondary}
            onClick={openCreate}
          >
            <FiPlus size={14} /> New Recurring Invoice
          </button>
        </div>
      </div>

      {/* Stats */}
      {showStats && (
        <div style={{ ...styles.card, background: colors.stone800, border: `1px solid ${colors.stone700}` }}>
          <div style={{ padding: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#fff' }}>Overview</span>
              <button style={{ ...styles.btnIcon, color: colors.stone400 }} onClick={() => setShowStats(false)} title="Hide Stats"><FiEyeOff size={14} /></button>
            </div>
            <div style={styles.statsGrid}>
              {/* Status Card */}
              <div style={styles.statCard}>
                <div style={styles.statTitle}>Status Breakdown</div>
                <SegmentBar segments={[
                  { value: statusCounts.ACTIVE, color: matte.green, title: 'Active', onClick: () => applyStatFilters({ status: 'ACTIVE' }) },
                  { value: statusCounts.PAUSED, color: matte.amber, title: 'Paused', onClick: () => applyStatFilters({ status: 'PAUSED' }) },
                  { value: statusCounts.CANCELLED, color: matte.red, title: 'Cancelled', onClick: () => applyStatFilters({ status: 'CANCELLED' }) },
                ]} />
                <div style={styles.legendGrid}>
                  <LegendItem label="Active" value={statusCounts.ACTIVE} color={matte.green} active={isStatActive('ACTIVE')} onClick={() => applyStatFilters({ status: 'ACTIVE' })} />
                  <LegendItem label="Paused" value={statusCounts.PAUSED} color={matte.amber} active={isStatActive('PAUSED')} onClick={() => applyStatFilters({ status: 'PAUSED' })} />
                  <LegendItem label="Cancelled" value={statusCounts.CANCELLED} color={matte.red} active={isStatActive('CANCELLED')} onClick={() => applyStatFilters({ status: 'CANCELLED' })} />
                  <LegendItem label="All" value={total} color={matte.grey} active={isStatActive('ALL')} onClick={() => applyStatFilters({ status: '' })} />
                </div>
              </div>
              {/* Due Card */}
              <div style={styles.statCard}>
                <div style={styles.statTitle}>Next Proforma Due</div>
                <SegmentBar segments={[
                  { value: dueCounts.overdue, color: matte.red, title: 'Overdue' },
                  { value: dueCounts.dueToday, color: matte.amber, title: 'Due Today' },
                  { value: dueCounts.upcoming, color: matte.blue, title: 'Upcoming' },
                ]} />
                <div style={styles.legendGrid}>
                  <LegendItem label="Overdue" value={dueCounts.overdue} color={matte.red} active={false} onClick={null} />
                  <LegendItem label="Due Today" value={dueCounts.dueToday} color={matte.amber} active={false} onClick={null} />
                  <LegendItem label="Upcoming" value={dueCounts.upcoming} color={matte.blue} active={false} onClick={null} />
                  <LegendItem label="Total" value={rows.length} color={matte.grey} active={false} onClick={null} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {!showStats && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button style={styles.btnSecondary} onClick={() => setShowStats(true)}><FiChevronDown size={14} /> Show Stats</button>
        </div>
      )}

      {/* Filters */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', flex: 1 }}>
            <div style={styles.searchWrapper}>
              <FiSearch size={14} style={styles.searchIcon} />
              <input
                style={styles.searchInput}
                placeholder="Search customer, template, proforma, invoice..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              />
            </div>
            <select style={styles.select} value={filters.customerId} onFocus={() => { if (!customersLoading && customers.length === 0) fetchCustomers(); }} onChange={e => { setFilters(f => ({ ...f, customerId: e.target.value })); setPage(1); }}>
              <option value="">All Customers</option>
              {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <select style={styles.select} value={statusView} onChange={e => setStatusView(e.target.value)}>
              <option value="">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="PAUSED">Paused</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>
          <button type="button" style={styles.btnSecondary} onClick={() => { setFilters(f => ({ ...f, customerId: '' })); setStatusView(''); setSearch(''); setPage(1); }}>
            <FiX size={14} /> Clear
          </button>
        </div>
      </div>

      {/* Table */}
      <div style={styles.card}>
        <div style={styles.tableWrap}>
          <table className="billing-table" style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Interval</th>
                <th style={styles.th}>Last Proforma</th>
                <th style={styles.th}>Last Invoice</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
                <th style={styles.th}>Last Created</th>
                <th style={styles.th}>Next Due</th>
                <th style={{ ...styles.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center' }}>
                  <div style={{ display: 'inline-block', width: 20, height: 20, border: `2px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                </td></tr>
              )}
              {!loading && !visibleRows.length && (
                <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: colors.stone400, fontSize: 12 }}>No recurring invoices found</td></tr>
              )}
              {!loading && visibleRows.map(s => (
                <tr key={s.id} className="billing-row" style={{ transition: 'background 0.15s' }}>
                  <td style={{ ...styles.td, fontWeight: 600 }}>
                    {(s.customer?.id || s.customerSnapshot?.id || s.customerId) ? (
                      <button style={styles.linkBtn} onClick={() => openCustomerDetail(s)}>{s.customer?.name || s.customerSnapshot?.name || getCustomerName(s.customerId)}</button>
                    ) : (s.customer?.name || s.customerSnapshot?.name || getCustomerName(s.customerId))}
                  </td>
                  <td style={styles.td}>
                    <span style={styles.statusWithDot}>
                      <span style={styles.statusDot(getStatusDotColor(s.status))} />
                      {s.name || '-'}
                    </span>
                  </td>
                  <td style={{ ...styles.td, fontSize: 12 }}>
                    <span style={{ color: colors.stone700 }}>{s.billingInterval}</span>
                    {Number(s.intervalCount) > 1 && <span style={{ color: colors.stone400 }}> ×{s.intervalCount}</span>}
                  </td>
                  <td style={{ ...styles.td, fontSize: 12 }}>
                    {(s.lastProformaNumber && s.lastProformaId) ? (
                      <button style={{ ...styles.linkBtn, ...proformaNumberStyle(s) }} onClick={() => openProformaPdf(s.lastProformaId, s.lastProformaNumber)}>{s.lastProformaNumber}</button>
                    ) : <span style={{ color: colors.stone400 }}>-</span>}
                  </td>
                  <td style={{ ...styles.td, fontSize: 12 }}>
                    {(s.lastInvoiceNumber && s.lastInvoiceId) ? (
                      <button style={{ ...styles.linkBtn, color: colors.emerald600 }} onClick={() => openInvoicePdf(s.lastInvoiceId, s.lastInvoiceNumber)}>{s.lastInvoiceNumber}</button>
                    ) : <span style={{ color: colors.stone400 }}>-</span>}
                  </td>
                  <td style={{ ...styles.td, textAlign: 'right', fontWeight: 600, color: colors.stone800, fontFeatureSettings: '"tnum"' }}>
                    <button style={styles.linkBtn} onClick={() => openPaymentDetails(s)}>{formatINR(calcTemplateTotal(s))}</button>
                  </td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 12, color: colors.stone500 }}>{s.lastInvoiceDate || <span style={{ color: colors.stone400 }}>-</span>}</td>
                  <td style={{ ...styles.td, whiteSpace: 'nowrap', fontSize: 12, color: colors.stone500 }}>{s.nextInvoiceDate || <span style={{ color: colors.stone400 }}>-</span>}</td>
                  <td style={{ ...styles.td, textAlign: 'right' }}>
                    <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                    {isLastProformaUnpaid(s) && (
                        <button
                          style={{ ...styles.btnIcon, color: colors.rose600 }}
                          onClick={() => setProformaPayModal({ open: true, proformaId: s.lastProformaId })}
                          title="Collect payment (convert last proforma to paid invoice)"
                        >
                          <FiCreditCard size={14} />
                        </button>
                      )}
                       {(() => {
                        const today = new Date().toISOString().slice(0, 10);
                        const eligible = (s.status === 'ACTIVE') && (s.items && s.items.length > 0) && (!(s.nextInvoiceDate && s.nextInvoiceDate > today));
                        if (!eligible) return null;
                        return <button style={{ ...styles.btnIcon, color: colors.blue600 }} onClick={() => invoiceNow(s.id)} title="Create Proforma now"><FiZap size={14} /></button>;
                      })()}
                      <button style={styles.btnIcon} onClick={() => navigate(`/recurring-invoice/${s.id}/edit`)} title="Edit"><FiEdit2 size={14} /></button>
                      <button style={{ ...styles.btnIcon, color: colors.blue600 }} onClick={() => openDetails(s.id)} title="Details"><FiEye size={14} /></button>
                     
                      {s.status !== 'ACTIVE' && (
                        <button style={{ ...styles.btnIcon, color: colors.emerald600 }} onClick={() => updateStatus(s.id, 'ACTIVE')} disabled={statusUpdatingId === s.id} title="Activate"><FiPlay size={14} /></button>
                      )}
                      {s.status !== 'PAUSED' && (
                        <button style={{ ...styles.btnIcon, color: colors.amber600 }} onClick={() => updateStatus(s.id, 'PAUSED')} disabled={statusUpdatingId === s.id} title="Pause"><FiPause size={14} /></button>
                      )}
                      {s.status !== 'CANCELLED' && (
                        <button style={{ ...styles.btnIcon, color: colors.rose600 }} onClick={() => updateStatus(s.id, 'CANCELLED')} disabled={statusUpdatingId === s.id} title="Cancel"><FiXCircle size={14} /></button>
                      )}
                     
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {renderPagination()}
      </div>

      {/* PDF Preview Drawer */}
      {pdfPreview.open && (
        <div style={styles.drawer} onClick={closePdfPreview}>
          <div style={styles.drawerContent} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${colors.stone200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>{pdfPreview.kind} - {pdfPreview.label}</h3>
              <div style={{ display: 'flex', gap: 8 }}>
                {!!pdfPreview.url && <a href={pdfPreview.url} target="_blank" rel="noreferrer" style={{ ...styles.btnSecondary, textDecoration: 'none' }}><FiExternalLink size={14} /> Open</a>}
                <button style={styles.btnSecondary} onClick={closePdfPreview}>Close</button>
              </div>
            </div>
            <div style={{ flex: 1, background: colors.stone100 }}>
              {pdfPreview.loading ? (
                <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 24, height: 24, border: `2px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                </div>
              ) : pdfPreview.url ? (
                <iframe title="PDF" src={pdfPreview.url} style={{ width: '100%', height: '100%', border: 'none' }} />
              ) : <div style={{ padding: 20, color: colors.stone500 }}>No preview</div>}
            </div>
          </div>
        </div>
      )}

      {/* Customer Drawer */}
      {customerDrawer.open && (
        <div style={styles.drawer} onClick={closeCustomerDrawer}>
          <div style={{ ...styles.drawerContent, width: '70%', maxWidth: 1000 }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '14px 20px', borderBottom: `1px solid ${colors.stone200}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Customer</h3>
              <button style={styles.btnSecondary} onClick={closeCustomerDrawer}>Close</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
              <CustomerDetail embeddedCustomerId={customerDrawer.id} embeddedCustomer={customerDrawer.customer} />
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {details.open && (
        <div style={styles.modal} onClick={closeDetails}>
          <div style={{ ...styles.modalContent, maxWidth: 900 }} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Recurring Invoice Details</h3>
              <button style={styles.btnIcon} onClick={closeDetails}><FiX size={18} /></button>
            </div>
            <div style={styles.modalBody}>
              {details.loading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                  <div style={{ width: 24, height: 24, border: `2px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                </div>
              )}
              {!!details.error && !details.loading && (
                <div style={{ background: colors.rose50, color: colors.rose700, padding: '10px 14px', borderRadius: 8, fontSize: 12 }}>{details.error}</div>
              )}
              {!details.loading && details.data && (
                <div style={{ display: 'grid', gap: 16 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div><div style={styles.label}>Name</div><div style={{ fontSize: 13, fontWeight: 600, color: colors.stone800 }}>{details.data.recurringInvoice?.name}</div></div>
                    <div><div style={styles.label}>Customer</div><div style={{ fontSize: 13, color: colors.stone800 }}>{details.data.recurringInvoice?.customer?.name || '-'}</div></div>
                    <div><div style={styles.label}>Status</div><div style={{ fontSize: 13, color: colors.stone800 }}>{details.data.recurringInvoice?.status}</div></div>
                    <div><div style={styles.label}>Next Proforma</div><div style={{ fontSize: 13, color: colors.stone800 }}>{details.data.recurringInvoice?.nextInvoiceDate || '-'}</div></div>
                    <div><div style={styles.label}>End Date</div><div style={{ fontSize: 13, color: colors.stone800 }}>{details.data.recurringInvoice?.endDate || '-'}</div></div>
                  </div>
                  <div>
                    <div style={{ ...styles.label, marginBottom: 8 }}>Proformas</div>
                    <div style={{ maxHeight: 180, overflow: 'auto', border: `1px solid ${colors.stone200}`, borderRadius: 8 }}>
                      <table style={styles.table}>
                        <thead><tr><th style={styles.th}>Number</th><th style={styles.th}>Date</th><th style={styles.th}>Status</th><th style={{ ...styles.th, textAlign: 'right' }}>Total</th><th style={{ ...styles.th, textAlign: 'center' }}>Actions</th></tr></thead>
                        <tbody>
                          {(details.data.proformas || []).map(p => (
                            <tr key={p.id}><td style={styles.td}>{p.number}</td><td style={styles.td}>{p.date}</td><td style={styles.td}>{p.status}</td><td style={{ ...styles.td, textAlign: 'right' }}>{formatINR(p.total)}</td>
                              <td style={{ ...styles.td, textAlign: 'center' }}>
                                <div style={{ display: 'inline-flex', gap: 4 }}>
                                  <button style={styles.btnSecondary} onClick={() => openProformaPdf(p.id, p.number)}>PDF</button>
                                  {p.status !== 'CONVERTED' && <button style={styles.btnPrimary} onClick={() => setProformaPayModal({ open: true, proformaId: p.id, paymentMethod: '', paymentReference: '', paymentDate: new Date().toISOString().slice(0, 10), proofUrl: '', uploading: false })}>Pay</button>}
                                </div>
                              </td>
                            </tr>
                          ))}
                          {(!details.data.proformas || !details.data.proformas.length) && <tr><td colSpan={5} style={{ padding: 12, textAlign: 'center', color: colors.stone400 }}>No proformas</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div>
                    <div style={{ ...styles.label, marginBottom: 8 }}>Invoices</div>
                    <div style={{ maxHeight: 180, overflow: 'auto', border: `1px solid ${colors.stone200}`, borderRadius: 8 }}>
                      <table style={styles.table}>
                        <thead><tr><th style={styles.th}>Number</th><th style={styles.th}>Date</th><th style={styles.th}>Status</th><th style={{ ...styles.th, textAlign: 'right' }}>Total</th><th style={{ ...styles.th, textAlign: 'center' }}>View</th></tr></thead>
                        <tbody>
                          {(details.data.invoices || []).map(iv => (
                            <tr key={iv.id}><td style={styles.td}>{iv.number}</td><td style={styles.td}>{iv.date}</td><td style={styles.td}>{iv.status}</td><td style={{ ...styles.td, textAlign: 'right' }}>{formatINR(iv.total)}</td>
                              <td style={{ ...styles.td, textAlign: 'center' }}><button style={styles.btnSecondary} onClick={() => openInvoicePdf(iv.id, iv.number)}>PDF</button></td>
                            </tr>
                          ))}
                          {(!details.data.invoices || !details.data.invoices.length) && <tr><td colSpan={5} style={{ padding: 12, textAlign: 'center', color: colors.stone400 }}>No invoices</td></tr>}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div style={styles.modalFooter}><button style={styles.btnSecondary} onClick={closeDetails}>Close</button></div>
          </div>
        </div>
      )}

      {/* Payment Info Modal */}
      {payInfo.open && (
        <div style={styles.modal} onClick={closePayInfo}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: colors.stone800 }}>Invoice Payment Details</h3>
              <button style={styles.btnIcon} onClick={closePayInfo}><FiX size={18} /></button>
            </div>
            <div style={styles.modalBody}>
              {payInfo.loading && (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                  <div style={{ width: 24, height: 24, border: `2px solid ${colors.stone200}`, borderTopColor: colors.accent500, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                </div>
              )}
              {!!payInfo.error && !payInfo.loading && (
                <div style={{ background: colors.rose50, color: colors.rose700, padding: '10px 14px', borderRadius: 8, fontSize: 12 }}>{payInfo.error}</div>
              )}
              {!payInfo.loading && payInfo.inv && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div><div style={styles.label}>Invoice</div><div style={{ fontSize: 13, color: colors.stone800, fontWeight: 600 }}>{payInfo.inv.number || `#${payInfo.inv.id}`}</div></div>
                  <div><div style={styles.label}>Status</div><div style={{ fontSize: 13, color: colors.stone800 }}>{payInfo.inv.paymentStatus || payInfo.inv.status || '-'}</div></div>
                  <div><div style={styles.label}>Method</div><div style={{ fontSize: 13, color: colors.stone800 }}>{payInfo.inv.paymentMethod || '-'}</div></div>
                  <div><div style={styles.label}>Reference</div><div style={{ fontSize: 13, color: colors.stone800 }}>{payInfo.inv.paymentReference || '-'}</div></div>
                  <div><div style={styles.label}>Payment Date</div><div style={{ fontSize: 13, color: colors.stone800 }}>{payInfo.inv.paymentDate ? new Date(payInfo.inv.paymentDate).toISOString().slice(0, 10) : '-'}</div></div>
                  <div><div style={styles.label}>Amount Paid</div><div style={{ fontSize: 13, color: colors.stone800, fontWeight: 600 }}>{formatINR(payInfo.inv.amountPaid || 0)}</div></div>
                  <div style={{ gridColumn: '1 / -1' }}><div style={styles.label}>Total</div><div style={{ fontSize: 13, color: colors.stone800, fontWeight: 600 }}>{formatINR(payInfo.inv.total || 0)}</div></div>
                  {!!payInfo.inv.paymentProofUrl && (
                    <div style={{ gridColumn: '1 / -1', marginTop: 8 }}>
                      <div style={styles.label}>Proof</div>
                      <a href={payInfo.inv.paymentProofUrl} target="_blank" rel="noreferrer">
                        <img src={payInfo.inv.paymentProofUrl} alt="Proof" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: `1px solid ${colors.stone200}` }} />
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div style={styles.modalFooter}><button style={styles.btnSecondary} onClick={closePayInfo}>Close</button></div>
          </div>
        </div>
      )}

      {/* Format Modal */}
      {formatModal && (
        <div style={styles.modal} onClick={() => setFormatModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}><h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Proforma Number Format</h3><button style={styles.btnIcon} onClick={() => setFormatModal(false)}><FiX size={18} /></button></div>
            <div style={styles.modalBody}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 120px 1fr', gap: 12 }}>
                <div style={styles.formGroup}><label style={styles.label}>Prefix</label><input style={styles.input} value={seriesState.prefixPre} onChange={e => setSeriesState(s => ({ ...s, prefixPre: e.target.value.replace(seriesState.fy, '').replace(new RegExp(`${seriesState.fySep}$`), '') }))} placeholder="PI" /></div>
                <div style={styles.formGroup}><label style={styles.label}>FY</label><input style={{ ...styles.input, background: colors.stone100, color: colors.stone500 }} value={seriesState.fy ? `${seriesState.fySep}${seriesState.fy}${seriesState.fySep}` : '-'} readOnly /></div>
                <div style={styles.formGroup}><label style={styles.label}>Suffix</label><input style={styles.input} value={seriesState.suffix} onChange={e => setSeriesState(s => ({ ...s, suffix: e.target.value }))} placeholder="-A" /></div>
              </div>
              <div style={{ marginTop: 8, fontSize: 12, color: colors.stone500 }}>
                Example: <span style={{ fontFamily: 'monospace', color: colors.stone700 }}>{seriesState.prefixPre}{seriesState.fySep}{seriesState.fy}{seriesState.fySep}{String((seriesState.currentSeq || 0) + 1).padStart(6, '0')}{seriesState.suffix}</span>
              </div>
              {!!formatOptions.length && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: colors.stone700, marginBottom: 8 }}>Existing Formats</div>
                  <div style={{ maxHeight: 160, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {formatOptions.map(r => {
                      const fy = r.fyStart && r.fyEnd ? `${String(new Date(r.fyStart).getUTCFullYear()).slice(-2)}-${String(new Date(r.fyEnd).getUTCFullYear()).slice(-2)}` : '';
                      return (
                        <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 10px', background: colors.stone50, border: `1px solid ${colors.stone200}`, borderRadius: 8, fontSize: 11 }}>
                          <div><span style={{ fontFamily: 'monospace' }}>{r.prefix}</span>{r.suffix && <span> <b>Suffix:</b> {r.suffix}</span>}{r.isActive && <span style={{ marginLeft: 8, padding: '2px 6px', background: colors.emerald50, color: colors.emerald700, borderRadius: 4, fontSize: 10 }}>Active</span>}</div>
                          <button style={styles.btnSecondary} onClick={() => applyExistingFormat(r)} disabled={r.isActive || formatActivatingId === r.id}>{r.isActive ? 'In use' : formatActivatingId === r.id ? '...' : 'Use'}</button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div style={styles.modalFooter}><button style={styles.btnSecondary} onClick={() => setFormatModal(false)}>Cancel</button><button style={styles.btnSecondary} onClick={saveSeriesFormat} disabled={seriesState.saving}>{seriesState.saving ? 'Saving...' : 'Save'}</button></div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {modal.open && (
        <div style={styles.modal} onClick={closeModal}>
          <div style={{ ...styles.modalContent, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}><h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Create Recurring Invoice</h3><button style={styles.btnIcon} onClick={closeModal}><FiX size={18} /></button></div>
            <div style={styles.modalBody}>
              {!!modal.error && <div style={{ background: colors.rose50, color: colors.rose700, padding: '10px 14px', borderRadius: 8, fontSize: 12, marginBottom: 12 }}>{modal.error}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div style={styles.formGroup}><label style={styles.label}>Name</label><input style={styles.input} placeholder="e.g., Monthly Maintenance" value={modal.form.name} onChange={e => setModal(m => ({ ...m, form: { ...m.form, name: e.target.value } }))} /></div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Customer</label>
                  <select style={styles.select} value={modal.form.customerId} onFocus={() => { if (!customersLoading && customers.length === 0) fetchCustomers(); }} onChange={e => setModal(m => ({ ...m, form: { ...m.form, customerId: e.target.value } }))}>
                    <option value="">Select customer</option>
                    {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Billing Interval</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <select style={styles.select} value={modal.form.billingInterval} onChange={e => setModal(m => ({ ...m, form: { ...m.form, billingInterval: e.target.value } }))}>
                      <option value="MONTH">Monthly</option>
                      <option value="QUARTER">Quarterly</option>
                      <option value="YEAR">Yearly</option>
                      <option value="CUSTOM">Custom</option>
                    </select>
                    <input style={styles.input} type="number" max={getIntervalMax(modal.form.billingInterval)} placeholder="Count" value={modal.form.intervalCount} onChange={e => { let v = Number(e.target.value); if (v > getIntervalMax(modal.form.billingInterval)) v = getIntervalMax(modal.form.billingInterval); setModal(m => ({ ...m, form: { ...m.form, intervalCount: v } })); }} />
                  </div>
                  <div style={{ fontSize: 11, color: colors.stone500, marginTop: 4 }}>{getIntervalExample(modal.form.billingInterval, modal.form.intervalCount)}</div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Bill on</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <select style={styles.select} value={modal.form.billingAnchor} onChange={e => setModal(m => ({ ...m, form: { ...m.form, billingAnchor: e.target.value } }))}>
                      <option value="ANNIVERSARY">Anniversary</option>
                      <option value="DAY_OF_MONTH">Day of Month</option>
                    </select>
                    <input style={styles.input} type="number" min="1" max="28" placeholder="Day (1-28)" value={modal.form.dayOfMonth} disabled={modal.form.billingAnchor !== 'DAY_OF_MONTH'} onChange={e => setModal(m => ({ ...m, form: { ...m.form, dayOfMonth: e.target.value ? Number(e.target.value) : '' } }))} />
                  </div>
                </div>
                <div style={styles.formGroup}><label style={styles.label}>Start Date</label><input style={styles.input} type="date" value={modal.form.startDate} onChange={e => setModal(m => ({ ...m, form: { ...m.form, startDate: e.target.value } }))} /></div>
                <div style={styles.formGroup}><label style={styles.label}>End Date (optional)</label><input style={styles.input} type="date" value={modal.form.endDate || ''} onChange={e => setModal(m => ({ ...m, form: { ...m.form, endDate: e.target.value } }))} /></div>
                <div style={styles.formGroup}><label style={styles.label}>Due Days</label><input style={styles.input} type="number" min="0" value={modal.form.dueDays} onChange={e => setModal(m => ({ ...m, form: { ...m.form, dueDays: Number(e.target.value) } }))} /></div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Currency</label>
                  <select style={styles.select} value={modal.form.currency} onChange={e => setModal(m => ({ ...m, form: { ...m.form, currency: e.target.value } }))}>
                    <option value="INR">INR</option>
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>
            </div>
            <div style={styles.modalFooter}><button style={styles.btnSecondary} onClick={closeModal}>Cancel</button><button style={styles.btnSecondary} onClick={save} disabled={modal.saving}>{modal.saving ? 'Saving...' : 'Next'}</button></div>
          </div>
        </div>
      )}

      {/* Proforma Pay Modal */}
      <PaymentModal
        isOpen={proformaPayModal.open}
        onClose={() => setProformaPayModal({ open: false, proformaId: null })}
        onSubmit={submitProformaPayment}
        title="Convert to Paid Invoice"
        submitText="Create Invoice"
        submitLoadingText="Creating..."
        acceptPdfProof={true}
      />
    </div>
  );
}
